import PropTypes from "prop-types"
import React, { use, useRef, useEffect } from "react"

// This script injector class exists so that if there are multiple players
// on one page, they will all wait on the same script element to load.
//
// It's also extremely important that this script injection only occurs inside
// the YouTubeEmbed component (which has Didomi and LazyLoad guards around it)
// so if you get tempted to export and re-use this class, be very aware of the
// privacy implications. As soon as this script is injected, Google puts tracking
// cookies on the users device.
class YouTubeScriptInjector {
  static get YT() {
    delete this.YT

    this.YT = new Promise((resolve, reject) => {
      // Handles unusual page states that can occur when mixing browser
      // level caching and turbo navigations, where the turbo navigation
      // causes the player API to have to re-init, but the old callback
      // still exists on window.
      //
      // First of all, even if the script tag was removed by a turbo
      // navigation, the player may still have already been loaded
      // so we can just return that right away.
      //
      // This is also intentionally an early return.
      if (window.YT && typeof window.YT.Player === "function") return resolve(window.YT)

      // Second, handle cases where the callback was already defined
      // but the player may not have initialized and resolved previously.
      const existingCallback = window.onYouTubeIframeAPIReady

      window.onYouTubeIframeAPIReady = () => {
        try {
          if (typeof existingCallback === "function") existingCallback()
        } finally {
          resolve(window.YT)
        }
      }

      // We can't just render a <script> tag directly from JSX, so we need to
      // inject one into the page if we haven't already done so.
      //
      // See `InstagramEmbed` for a similar approach
      //
      // It also fairly closely follows the guidance from Google in:
      //
      //     https://developers.google.com/youtube/player_parameters
      //
      // But with some enhancements to ensure there's only ever one Player API
      // initialized per page.
      //
      // This probably needs to change but I'm wary of biting off a bigger
      // refactor (since they all will need to change) right now. This method
      // just turns out to not be very turbo friendly.
      const existingScriptElement = document.getElementById("injected-youtube-embed")

      if (!existingScriptElement) {
        const scriptElement = document.createElement("script")
        scriptElement.id = "injected-youtube-embed"
        scriptElement.src = "https://www.youtube.com/iframe_api"
        scriptElement.async = true
        scriptElement.onerror = () => reject(new Error("Failed to load YouTube Player API"))
        document.body.appendChild(scriptElement)
      }
    })

    return this.YT
  }
}

const YouTubeEmbed = ({ videoId, start = 0 }) => {
  const YT = use(YouTubeScriptInjector.YT)
  const playerRef = useRef(null)
  const elementRef = useRef(null)

  useEffect(() => {
    if (!elementRef.current || playerRef.current) return

    playerRef.current = new YT.Player(elementRef.current, {
      videoId,
      playerVars: {
        start,
      },
      events: {
        // This is a hack to work around our existing lazyloading code interfering
        // with the iframe styling.
        onReady: (event) => event.target.getIframe().classList.add("native-lazy"),
      },
    })

    return () => {
      playerRef.current?.destroy()
      playerRef.current = null
    }
  }, [YT, videoId, start])

  return <div ref={elementRef} />
}

YouTubeEmbed.propTypes = {
  videoId: PropTypes.string,
  start: PropTypes.number,
}

export default YouTubeEmbed
